/**
 * Main Application Entry Point — Donor Portal
 *
 * Initializes the Vue application with all necessary plugins,
 * stores, and configuration.
 */
import { createApp } from 'vue';
import { initializeLogging, initializeTelemetry, createLogger, toTitleCase } from '@tms-phoenix/ui-vue';
import App from './App.vue';
import { router } from './router/instance';
import { routes } from './router';
import { authGuard, roleGuard, logoutGuard } from './router/guards';
import { setupPinia, setupVueQuery, setupI18n } from './core/plugins';
import { config } from './core/config';
import { useUIStore } from './stores';
import { useAuthStore } from './stores';

// Import Tailwind CSS
import './styles/main.css';

// PrimeVue v4 config
import PrimeVue from 'primevue/config';
import ToastService from 'primevue/toastservice';
import Tooltip from 'primevue/tooltip';

// Pick a theme preset from @primeuix/themes (Aura, Lara, Nora, etc.)
import Aura from '@primeuix/themes/aura';

// Initialize logging first
initializeLogging(config.logging);

// Initialize telemetry
initializeTelemetry(config.telemetry);

const logger = createLogger('main');
logger.info('Initializing Donor Portal application', {
  mode: import.meta.env.MODE,
  version: config.app.version,
});

// Create Vue app instance
const app = createApp(App);

// Setup core plugins
setupPinia(app);
setupVueQuery(app);

// Setup i18n (vue-i18n) — lazy-loads locale bundles from @tms-phoenix/tms-types
await setupI18n(app);

// Setup PrimeVue
app.use(PrimeVue, {
  theme: {
    preset: Aura,
    options: {
      darkModeSelector: false,
      cssLayer: {
        name: 'primevue',
        order: 'theme, base, primevue'
      }
    }
  }
});
app.use(ToastService);
app.directive('tooltip', Tooltip);
app.config.globalProperties.$titleCase = toTitleCase;

// Initialize UI store after Pinia is set up
const uiStore = useUIStore();
uiStore.initializeFromViewport();

// Re-hydrate auth-derived config (e.g. personId) from persisted store state
// so runtime config is correct after a page reload.
useAuthStore().initAuthConfig();

// Setup router — add full route table to the singleton (avoids circular deps)
routes.forEach((route) => {
  if (!route.name || !router.hasRoute(route.name)) {
    router.addRoute(route);
  }
});

// Apply global guards
// Captured here because vue-router@4 router.onError only receives the error argument.
let pendingNavPath = location.pathname;
router.beforeEach((to, from, next) => {
  pendingNavPath = to.fullPath;
  logger.debug('Navigation start', { from: from.path, to: to.path });
  authGuard(to, from, next);
});
router.beforeEach(roleGuard);
router.beforeEach(logoutGuard);

router.afterEach((to, from) => {
  logger.debug('Navigation complete', { from: from.path, to: to.path });
});

router.onError((error) => {
  logger.error('Router error', { error });

  const isChunkLoadError =
    error?.message?.includes('Failed to fetch dynamically imported module') ||
    error?.message?.includes('Importing a module script failed') ||
    error?.message?.includes('dynamically imported module');

  if (isChunkLoadError) {
    const reloadKey = `chunk-reload:${pendingNavPath}`;
    if (!sessionStorage.getItem(reloadKey)) {
      sessionStorage.setItem(reloadKey, '1');
      window.location.href = pendingNavPath;
    }
  }
});

window.addEventListener('vite:preloadError', () => {
  const reloadKey = `vite-preload-reload:${location.pathname}`;
  if (!sessionStorage.getItem(reloadKey)) {
    sessionStorage.setItem(reloadKey, '1');
    window.location.reload();
  }
});

app.use(router);

// Mount application
app.mount('#app');

logger.info('Donor Portal application mounted successfully');
